import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully!');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="contact-page">
      {/* Hero */}
      <section className="bg-leather-900 px-6 md:px-12 lg:px-24 py-24 text-center">
        <h1 className="font-heading text-4xl md:text-5xl text-white mb-4">Contact Us</h1>
        <p className="text-cream-100/70 max-w-lg mx-auto">
          We'd love to hear from you. Reach out with questions, feedback, or just to say hello.
        </p>
      </section>

      <section className="px-6 md:px-12 lg:px-24 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 max-w-6xl mx-auto">
          {/* Contact Info */}
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-leather-400 mb-4">Get in Touch</p>
            <h2 className="font-heading text-3xl text-leather-900 mb-8">We're Here to Help</h2>
            
            <div className="space-y-8">
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-cream-100 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-leather-900" />
                </div>
                <div>
                  <h3 className="font-medium text-leather-900 mb-1">Address</h3>
                  <p className="text-leather-600 text-sm">
                    Kingston Jamaica WI
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 bg-cream-100 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-leather-900" />
                </div>
                <div>
                  <h3 className="font-medium text-leather-900 mb-1">Phone</h3>
                  <p className="text-leather-600 text-sm">+18763820841</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 bg-cream-100 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-leather-900" />
                </div>
                <div>
                  <h3 className="font-medium text-leather-900 mb-1">Email</h3>
                  <p className="text-leather-600 text-sm">shop@leatherbylindsay.com</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 bg-cream-100 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-leather-900" />
                </div>
                <div>
                  <h3 className="font-medium text-leather-900 mb-1">Business Hours</h3>
                  <p className="text-leather-600 text-sm">
                    Monday - Friday: 9am - 6pm EST<br />
                    Saturday: 10am - 4pm EST<br />
                    Sunday: Closed
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-cream-100 p-8 md:p-10">
            <h3 className="font-heading text-2xl text-leather-900 mb-6">Send a Message</h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="text-xs tracking-widest uppercase text-leather-600 block mb-2">
                  Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-flushed w-full bg-transparent"
                  required
                  data-testid="contact-name"
                />
              </div>

              <div>
                <label className="text-xs tracking-widest uppercase text-leather-600 block mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input-flushed w-full bg-transparent"
                  required
                  data-testid="contact-email"
                />
              </div>

              <div>
                <label className="text-xs tracking-widest uppercase text-leather-600 block mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="input-flushed w-full bg-transparent"
                  required
                  data-testid="contact-subject"
                />
              </div>

              <div>
                <label className="text-xs tracking-widest uppercase text-leather-600 block mb-2">
                  Message
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="input-flushed w-full bg-transparent min-h-[150px] resize-none"
                  required
                  data-testid="contact-message"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full btn-primary disabled:opacity-50"
                data-testid="contact-submit"
              >
                {submitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
